/**
 * Widgets — o botão de conversa no site do cliente.
 *
 * ## A frase que esta tela precisa dizer, e diz
 *
 * A mensagem pré-determinada **não é enfeite**: é ela que permite contar quantas conversas vieram do
 * botão. Sem ela, a conversa chega igual a qualquer outra. Por isso o campo é obrigatório e a explicação
 * fica junto dele (no (i) do campo), não num rodapé.
 *
 * ## E a honestidade sobre o número
 *
 * O que se conta é **conversa**, não clique. Quem clica e não envia não aparece — e a tela diz isso, em
 * vez de deixar o cliente comparar com o Google Analytics dele e achar que o número está errado.
 *
 * ## A forma (spec 22)
 *
 * Cada aba de Integrações (botões, webhooks, chaves) entrega DUAS partes: a lista (`ListView` do kit,
 * Nome · Tipo · Estado · Criado · Fim) e o painel do escolhido ou do que se cria. Quem monta a grade é
 * `Integracoes.tsx` (`montar`); as peças comuns às três abas moram aqui (`ListaDaAba`, `CampoComDica`).
 */

import { useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { falhaDe, identificadorLegivel, type LinhaDaListagem } from "@hugi/ui";
import { CampoDoCadastro, Dica, ListView, Selo, Vazio, type ColunaDaLista, type Tom, type VistaDaLista } from "@hugi/ui/kit";
import {
  arquivarWidget,
  criarWidget,
  useCatalogos,
  useConexoes,
  useWidgets,
  type Widget,
} from "./dados.js";
import "./integracoes.css";

/* ============================== peças comuns às abas ============================== */

/** O painel à direita da lista: o escolhido, o que se cria, ou o segredo recém-criado. */
export interface PainelDaAba {
  readonly titulo: string;
  readonly conteudo: ReactNode;
  /** As ações, fixas embaixo do cartão (fora do que rola). */
  readonly rodape?: ReactNode;
}

export interface PartesDaAba {
  readonly lista: ReactNode;
  readonly painel: PainelDaAba;
}

export interface PropsDaAba {
  /** Monta a tela com as duas partes (a grade é de `Integracoes.tsx`). */
  readonly montar: (partes: PartesDaAba) => ReactNode;
  /** No celular, leva ao cartão do painel quando a pessoa escolhe ou cria. */
  readonly abrirPainel: () => void;
}

const TOM: Readonly<Record<LinhaDaListagem["estado"]["tom"], Tom>> = {
  ok: "sucesso",
  degradado: "atencao",
  ruim: "perigo",
  neutro: "neutro",
};

const data = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");
const instante = (iso: string | null) => (iso ? new Date(iso).getTime() : null);

const COLUNAS: ColunaDaLista<LinhaDaListagem>[] = [
  { id: "nome", rotulo: "Nome", principal: true, link: true, dica: (l) => l.nome, celula: (l) => l.nome, ordenar: (l) => l.nome },
  { id: "tipo", rotulo: "Tipo", celula: (l) => l.tipo, ordenar: (l) => l.tipo },
  {
    id: "estado",
    rotulo: "Estado",
    celula: (l) => <Selo tom={TOM[l.estado.tom]}>{l.estado.rotulo}</Selo>,
    ordenar: (l) => l.estado.rotulo,
  },
  { id: "criado", rotulo: "Criado", numerica: true, celula: (l) => data(l.criadoEm), ordenar: (l) => instante(l.criadoEm) },
  { id: "fim", rotulo: "Fim", numerica: true, celula: (l) => data(l.fimEm), ordenar: (l) => instante(l.fimEm) },
];

/**
 * A lista de uma aba: a mesma tabela para tudo que o cliente cria (regra do owner de 2026-09-03 —
 * nome, tipo, estado, criado, fim), com busca e as vistas por estado.
 */
export function ListaDaAba(props: {
  readonly titulo: string;
  readonly memoria: string;
  readonly linhas: readonly LinhaDaListagem[] | undefined;
  readonly carregando: boolean;
  readonly erro: unknown;
  readonly aoTentarDeNovo: () => void;
  readonly escolhido: string | null;
  readonly aoEscolher: (id: string) => void;
  readonly vazio: string;
  readonly contagem: (n: number) => string;
  readonly acoes?: ReactNode;
}) {
  const estados = useMemo(
    () => [...new Set((props.linhas ?? []).map((l) => l.estado.rotulo))].sort(),
    [props.linhas],
  );
  const vistas: VistaDaLista<LinhaDaListagem>[] | undefined =
    estados.length > 1
      ? [
          { id: "todos", rotulo: "Todos", passa: () => true },
          ...estados.map((e) => ({ id: e, rotulo: e, passa: (l: LinhaDaListagem) => l.estado.rotulo === e })),
        ]
      : undefined;
  const falha = props.erro ? falhaDe(props.erro) : null;
  return (
    <ListView<LinhaDaListagem>
      titulo={props.titulo}
      colunas={COLUNAS}
      linhas={props.linhas ? [...props.linhas] : undefined}
      chave={(l) => l.id}
      memoria={props.memoria}
      {...(vistas ? { vistas } : {})}
      estado={{
        carregando: props.carregando,
        erro: Boolean(props.erro) && falha?.tipo !== "sem_permissao",
        semPermissao: falha?.tipo === "sem_permissao",
        ...(falha?.erro.mensagem ? { mensagemDeErro: falha.erro.mensagem } : {}),
        aoTentarDeNovo: props.aoTentarDeNovo,
      }}
      busca={{ campos: (l) => [l.nome, l.tipo, l.busca], placeholder: "Buscar" }}
      selecao={{ selecionada: props.escolhido, aoSelecionar: (l) => props.aoEscolher(l.id) }}
      contagem={props.contagem}
      vazio={{ semNada: props.vazio, semResultado: "Nada com essa busca nesta vista." }}
      {...(props.acoes ? { acoes: props.acoes } : {})}
    />
  );
}

type PropsDoControle = {
  id: string;
  "aria-invalid": true | undefined;
  "aria-describedby": string | undefined;
  required: boolean | undefined;
};

/** Um campo do formulário com o (i) ao lado do rótulo: a explicação mora no (i), não em parágrafo. */
export function CampoComDica(props: {
  readonly rotulo: string;
  readonly dica?: string;
  readonly ajuda?: string;
  readonly largo?: boolean;
  readonly children: (p: PropsDoControle) => ReactNode;
}) {
  return (
    <div className={`hugi-integracoes-campo${props.largo ? " is-largo" : ""}`}>
      <CampoDoCadastro rotulo={props.rotulo} {...(props.ajuda ? { ajuda: props.ajuda } : {})} largo={props.largo ?? false}>
        {(p) => props.children(p)}
      </CampoDoCadastro>
      {props.dica ? (
        <span className="hugi-integracoes-dica">
          <Dica texto={props.dica} />
        </span>
      ) : null}
    </div>
  );
}

/** O painel vazio: nada escolhido ainda. */
export function NadaEscolhido(props: { readonly children: ReactNode }) {
  return <Vazio>{props.children}</Vazio>;
}

/* ============================== Botões do site ============================== */

function linhaDoWidget(w: Widget): LinhaDaListagem {
  return {
    id: w.id,
    nome: w.nome,
    tipo: w.canal ?? "Primeiro canal",
    estado: w.ativo ? { rotulo: "Ativo", tom: "ok" } : { rotulo: "Desativado", tom: "ruim" },
    criadoEm: w.criadoEm,
    fimEm: null,
    busca: w.mensagem,
  };
}

/** Botão de conversa no site. Um link, não um script: nada nosso roda no site do cliente. */
export function Widgets(props: PropsDaAba) {
  const cliente = useQueryClient();
  const q = useWidgets();
  const conexoes = useConexoes();
  const catalogos = useCatalogos();
  const [escolhido, setEscolhido] = useState<string | null>(null);
  const [criandoBotao, setCriandoBotao] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [nome, setNome] = useState("");
  const [mensagem, setMensagem] = useState("Olá! Vim pelo site e gostaria de mais informações.");
  const [rotulo, setRotulo] = useState("Falar no WhatsApp");
  const [cor, setCor] = useState(() =>
    typeof getComputedStyle === "function"
      ? getComputedStyle(document.documentElement).getPropertyValue("--azul").trim()
      : "",
  );
  const [conexaoId, setConexaoId] = useState("");
  const [filaId, setFilaId] = useState("");
  const [etiquetaId, setEtiquetaId] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);

  const criar = async () => {
    setCriando(true);
    setErro(null);
    const r = await criarWidget(cliente, {
      nome,
      mensagem,
      rotulo,
      cor,
      conexaoId: conexaoId || null,
      filaId: filaId || null,
      etiquetaId: etiquetaId || null,
    });
    setCriando(false);
    if (r.ok) {
      setNome("");
      setCriandoBotao(false);
      return;
    }
    setErro(r.falha?.erro.mensagem ?? "Não foi possível criar.");
  };

  const lista = q.data?.itens ?? [];
  const w = lista.find((x) => x.id === escolhido) ?? null;
  const semMotivo = !nome.trim() || !mensagem.trim();

  let painel: PainelDaAba;
  if (criandoBotao) {
    painel = {
      titulo: "Novo botão",
      conteudo: (
        <div className="hugi-integracoes-form">
          <CampoComDica rotulo="Nome do botão" ajuda="Só para você reconhecer depois.">
            {(p) => <input {...p} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex.: rodapé do site" />}
          </CampoComDica>
          <CampoComDica rotulo="Texto do botão">
            {(p) => <input {...p} value={rotulo} onChange={(e) => setRotulo(e.target.value)} maxLength={40} />}
          </CampoComDica>
          <CampoComDica
            rotulo="Mensagem que o visitante envia"
            largo
            ajuda={`${mensagem.length} de 280.`}
            dica="É ela que permite contar quantas conversas vieram deste botão — a mensagem funciona como assinatura. Sem ela, a conversa chegaria igual a qualquer outra."
          >
            {(p) => (
              <textarea {...p} value={mensagem} onChange={(e) => setMensagem(e.target.value)} rows={2} maxLength={280} />
            )}
          </CampoComDica>
          <CampoComDica rotulo="Cor">
            {(p) => <input {...p} type="color" value={cor} onChange={(e) => setCor(e.target.value)} className="hugi-integracoes-cor" />}
          </CampoComDica>
          <CampoComDica rotulo="Canal">
            {(p) => (
              <select {...p} value={conexaoId} onChange={(e) => setConexaoId(e.target.value)}>
                <option value="">Primeiro canal ativo</option>
                {(conexoes.data?.conexoes ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.numeroExibicao ? identificadorLegivel(c.numeroExibicao) : c.canal} ({c.canal})
                  </option>
                ))}
              </select>
            )}
          </CampoComDica>
          <CampoComDica rotulo="Fila">
            {(p) => (
              <select {...p} value={filaId} onChange={(e) => setFilaId(e.target.value)}>
                <option value="">Fila padrão do canal</option>
                {(catalogos.data?.filas ?? []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </select>
            )}
          </CampoComDica>
          <CampoComDica
            rotulo="Etiqueta (opcional)"
            dica={'Marca quem chega por este botão. É o que faz "veio do site" aparecer no inbox e no relatório.'}
          >
            {(p) => (
              <select {...p} value={etiquetaId} onChange={(e) => setEtiquetaId(e.target.value)}>
                <option value="">Nenhuma</option>
                {(catalogos.data?.etiquetas ?? []).map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nome}
                  </option>
                ))}
              </select>
            )}
          </CampoComDica>
          {/* A prévia usa a cor e o texto escolhidos. */}
          <div className="hugi-integracoes-previa is-largo">
            <span>Fica assim:</span>
            <span className="hugi-integracoes-botao-previa" style={{ background: cor }}>
              {rotulo || "Falar no WhatsApp"}
            </span>
          </div>
        </div>
      ),
      rodape: (
        <div className="hugi-integracoes-acoes">
          {erro ? (
            <p role="alert" className="v2-erro">
              {erro}
            </p>
          ) : null}
          <button type="button" className="v2-botao" onClick={() => setCriandoBotao(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="v2-botao is-primario"
            onClick={() => void criar()}
            disabled={criando || semMotivo}
            title={criando || semMotivo ? "Dê um nome e uma mensagem." : undefined}
          >
            {criando ? "Criando…" : "Criar botão"}
          </button>
        </div>
      ),
    };
  } else if (w) {
    painel = {
      titulo: w.nome,
      conteudo: (
        <div className="hugi-integracoes-detalhe">
          <p className="hugi-integracoes-mudo">“{w.mensagem}”</p>
          <p>
            <strong>{w.conversas}</strong>{" "}
            <span className="hugi-integracoes-mudo">conversa(s) começaram por este botão.</span>
          </p>
          {w.trecho.aviso ? (
            <p role="alert" className="hugi-integracoes-aviso">
              {w.trecho.aviso}
            </p>
          ) : (
            <>
              <div className="hugi-integracoes-linha">
                <span className="hugi-integracoes-rotulo">Cole no seu site</span>
                <button
                  type="button"
                  className="v2-botao is-pequeno"
                  onClick={() => {
                    void navigator.clipboard?.writeText(w.trecho.html);
                    setCopiado(true);
                  }}
                >
                  {copiado ? "Copiado" : "Copiar"}
                </button>
              </div>
              <pre className="hugi-integracoes-codigo">
                <code>{w.trecho.html}</code>
              </pre>
            </>
          )}
        </div>
      ),
      ...(w.ativo
        ? {
            rodape: (
              <div className="hugi-integracoes-acoes">
                <button type="button" className="v2-botao is-perigo" onClick={() => void arquivarWidget(cliente, w.id)}>
                  Desativar
                </button>
              </div>
            ),
          }
        : {}),
    };
  } else {
    painel = { titulo: "Botão", conteudo: <NadaEscolhido>Escolha um botão na lista, ou crie um.</NadaEscolhido> };
  }

  return props.montar({
    lista: (
      <ListaDaAba
        titulo="Botões do site"
        memoria="hugi:integracoes:botoes"
        linhas={q.data ? lista.map(linhaDoWidget) : undefined}
        carregando={q.isPending}
        erro={q.error}
        aoTentarDeNovo={() => void q.refetch()}
        escolhido={escolhido}
        aoEscolher={(id) => {
          setEscolhido(id);
          setCriandoBotao(false);
          setCopiado(false);
          props.abrirPainel();
        }}
        vazio="Nenhum botão. Crie um e cole o trecho no seu site."
        contagem={(n) => (n === 1 ? "1 botão" : `${n} botões`)}
        acoes={
          <button
            type="button"
            className="v2-botao is-primario is-pequeno"
            onClick={() => {
              setCriandoBotao(true);
              setEscolhido(null);
              props.abrirPainel();
            }}
          >
            Criar botão
          </button>
        }
      />
    ),
    painel,
  });
}
